/** Формулы прогноза продаж — порт plan-части оригинала (build-скрипт):
 * planValue/planValueTotal (L6828/6899), currentPlanForRow (L6902), planStatusClass (L6923),
 * salesPlanChannels (L6880), monthsBetween (L3777), planMonthsForYear.
 * План хранится в БД портала ячейками (год, артикул, канал, месяц 1-12). */
import type { PlanChannel } from "@/api/orders-planning";
import type { Tab } from "./metrics";

/** Ключ ячейки плана в клиентской Map. */
export function planKey(article: string, channel: string, month: number): string {
  return `${article}|${channel}|${month}`;
}

export type PlanMap = Map<string, number>; // planKey → qty (в рамках одного года)

export function planValue(plan: PlanMap, article: string, channel: string, month: number): number {
  return plan.get(planKey(article, channel, month)) ?? 0;
}

export interface PlanChannelInfo {
  code: string;
  label: string;
  type: "mp" | "wholesale";
}

const MP_ORDER: PlanChannelInfo[] = [
  { code: "ozon", label: "Ozon", type: "mp" },
  { code: "wb", label: "Wildberries", type: "mp" },
  { code: "ym", label: "Яндекс.Маркет", type: "mp" },
];

/** Порт salesPlanChannels: каталог + гарантированные МП-каналы в начале. */
export function salesPlanChannels(catalog: PlanChannel[]): PlanChannelInfo[] {
  const mapped: PlanChannelInfo[] = catalog.map((c) => ({
    code: c.code,
    label: c.name,
    type: c.kind === "wholesale" ? "wholesale" : "mp",
  }));
  const present = new Set(mapped.map((c) => c.code));
  const prepend = MP_ORDER.filter((c) => !present.has(c.code));
  return [...prepend, ...mapped];
}

/** Сумма плана по всем каналам (порт planValueTotal). */
export function planValueTotal(
  plan: PlanMap,
  channels: PlanChannelInfo[],
  article: string,
  month: number,
): number {
  return channels.reduce((s, ch) => s + planValue(plan, article, ch.code, month), 0);
}

/** План строки для месяца текущей вкладки (порт currentPlanForRow). */
export function currentPlanForRow(
  plan: PlanMap,
  channels: PlanChannelInfo[],
  tab: Tab,
  article: string,
  month: number,
): number {
  if (tab === "all") return planValueTotal(plan, channels, article, month);
  return planValue(plan, article, tab, month);
}

export type PlanStatus = "" | "plan-ok" | "plan-warn" | "plan-bad";

/** Порт planStatusClass: ≥100% зелёный, ≥80% жёлтый, иначе красный; без плана — пусто. */
export function planStatusClass(pct: number, planQty: number): PlanStatus {
  if (!planQty) return "";
  if (pct >= 100) return "plan-ok";
  if (pct >= 80) return "plan-warn";
  return "plan-bad";
}

/** Порт monthsBetween: список YYYY-MM между двумя месяцами включительно. */
export function monthsBetween(fromMonth: string, toMonth: string): string[] {
  const result: string[] = [];
  let y = Number(fromMonth.slice(0, 4));
  let m = Number(fromMonth.slice(5, 7));
  const end = Number(toMonth.slice(0, 4)) * 12 + Number(toMonth.slice(5, 7));
  while (y * 12 + m <= end) {
    result.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return result;
}

/** 12 месяцев года (порт planMonthsForYear). */
export function planMonthsForYear(year: number): string[] {
  return Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);
}

/** Дефолтные границы планового периода (порт planDateBounds):
 * от min_date данных до 31 декабря года max_date. */
export function defaultPlanBounds(minDate: string, maxDate: string): { from: string; to: string } {
  return { from: minDate, to: `${maxDate.slice(0, 4)}-12-31` };
}

/** Годы, покрываемые списком месяцев (для загрузки планов по годам). */
export function yearsOfMonths(months: string[]): number[] {
  return [...new Set(months.map((m) => Number(m.slice(0, 4))))];
}
